import { useMutation } from "@apollo/client";
import {
  Button,
  Container,
  Icon,
  useConfirmDialog,
} from "@fluidattacks/design";
import { Fragment, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { REMOVE_STAKEHOLDER_MUTATION } from "../queries";
import {
  getAreAllMutationValid,
  handleRemoveError,
  removeMultipleAccess,
} from "../utils";
import { authContext } from "context/auth";
import type { IStakeholderDataSet } from "pages/organization/members/types";
import { useStoredState } from "storage/state";
import { msgSuccess } from "utils/notifications";

interface IRemoveActionProps {
  groupName: string;
  selectedMembers: IStakeholderDataSet[];
  resetOnAction: VoidFunction;
  disabled?: boolean;
}

const RemoveAction = (props: IRemoveActionProps): JSX.Element => {
  const { groupName, resetOnAction, disabled = false, selectedMembers } = props;
  const { t } = useTranslation();
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const [lastOrganization, setLastOrganization] = useStoredState(
    "organizationV2",
    { deleted: false, name: "" },
    "local",
  );
  const { userEmail } = useContext(authContext);

  const [removeStakeholderAccess] = useMutation(REMOVE_STAKEHOLDER_MUTATION, {
    onError: handleRemoveError,
  });

  const handleRemove = useCallback(async (): Promise<void> => {
    const results = await removeMultipleAccess(
      removeStakeholderAccess,
      selectedMembers,
      groupName,
    );

    const areMutationsValid = getAreAllMutationValid(results);

    if (areMutationsValid.every(Boolean) && areMutationsValid.length === 1) {
      msgSuccess(
        `${selectedMembers[0]?.email} ${t(
          "searchFindings.tabUsers.successDelete",
        )}`,
        t("searchFindings.tabUsers.titleSuccess"),
      );
    } else if (areMutationsValid.every(Boolean)) {
      msgSuccess(
        t("searchFindings.tabUsers.successDeletePlural"),
        t("searchFindings.tabUsers.titleSuccess"),
      );
    }

    if (
      selectedMembers.findIndex(
        (stakeholder): boolean => stakeholder.email === userEmail,
      ) > -1
    ) {
      setLastOrganization({ ...lastOrganization, deleted: true });
    }

    resetOnAction();
  }, [
    groupName,
    selectedMembers,
    removeStakeholderAccess,
    userEmail,
    resetOnAction,
    lastOrganization,
    setLastOrganization,
    t,
  ]);

  const onClick = useCallback(async (): Promise<void> => {
    const confirmResult = await confirm({
      message: t("searchFindings.tabUsers.removeUserButton.confirmMessage", {
        users: selectedMembers.map((user): string => user.email).join(", "),
      }),
      title: t("searchFindings.tabUsers.removeUserButton.confirmTitle"),
    });
    if (confirmResult) {
      await handleRemove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMembers]);

  return (
    <Fragment>
      <Button
        disabled={disabled}
        id={"members-remove-button"}
        justify={"start"}
        onClick={onClick}
        variant={"ghost"}
        width={"100%"}
      >
        <Container justify={"start"} pl={0.5}>
          <Icon
            icon={"trash"}
            iconSize={"lg"}
            iconType={"fa-light"}
            mr={0.25}
          ></Icon>
          {t("tables.members.buttons.delete.text")}
        </Container>
      </Button>
      <ConfirmDialog />
    </Fragment>
  );
};

export { RemoveAction };
